import React, { useEffect, useState } from "react";
import { Search, Filter } from "lucide-react";
import { getCustomerOrderList } from "../../services/customerServices";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Drawer from "../../components/Common/Drawer";
import {
  getAllCustomerStoreList,
} from "../../services/commonServices";
import { customStyles, formatCurrencyINR } from "../../utils/function";
import Select from "react-select";
import { notify } from "../../components/Common/Alerts";
import config from "../../config";
import { XCircle } from "react-bootstrap-icons";
import { useConfig } from "../../components/Context/ConfigContext";

const OrderManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { configData } = useConfig();

  const [orderList, setOrderList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    order_type: "",
    store_id: "",
    from_date: "",
    to_date: "",
  });
  const [tempFilters, setTempFilters] = useState({
    status: "",
    order_type: "",
    store_id: "",
    from_date: "",
    to_date: "",
  });
  const [storeList, setStoreList] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const getAppliedFilterCount = (filters) => {
    return Object.values(filters).filter((v) => v !== "" && v !== null).length;
  };

  const handleSearch = () => {
    setShowOffcanvas(false);
    setFilters(tempFilters); // apply the selected filters
    setPage(1); // trigger useEffect to fetch new data
  };

  const handleReset = () => {
    setFilters({
      status: "",
      order_type: "",
      store_id: "",
      from_date: "",
    });
    setTempFilters({
      status: "",
      order_type: "",
      store_id: "",
      from_date: "",
      to_date: "",
    });

    setHasMore(true);
    setLoading(false);
    setShowOffcanvas(false);
  };

  const handleOpen = () => {
    setShowOffcanvas(true);
  };



  useEffect(() => {
    const fetchStores = async () => {
      try {
        const data = { search: "" };
        const response = await getAllCustomerStoreList(data);

        const decrypted = decryptData(response.data.data);
        const dataList = decrypted.data || [];

        const storeOptions = dataList.map((store) => ({
          label: store.name,
          value: store.id,
        }));

        setStoreList(storeOptions);
      } catch (err) {
        notify.error(Utils.getErrorMessage(err));
      }
    };
    fetchStores();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);

        const data = {
          ...filters,
          search: debouncedSearch || "",
          page,
          limit: config.DATA_LIMIT,
        };

        const response = await getCustomerOrderList(data);
        const decrypted = decryptData(response.data.data);
        const newData = decrypted.data || [];

        setOrderList((prev) => (page === 1 ? newData : [...prev, ...newData]));
        setHasMore(newData.length >= 20);
      } catch (err) {
        notify.error(Utils.getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [debouncedSearch, filters, page]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollHeight - scrollTop <= clientHeight + 50 && // near bottom
      !loading &&
      hasMore
    ) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#FDFCFB] dark:bg-[#050C1A] font-sans overflow-hidden transition-colors duration-300">
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-[#050C1A]/80 backdrop-blur-md px-4 py-3 md:py-6 border-b border-slate-100 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-orange-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Search orders..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-[#161E2D] border border-slate-200 dark:border-slate-800 rounded-[1.2rem] text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 outline-none transition-all shadow-sm"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpen}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-white dark:bg-[#161E2D] border border-slate-200 dark:border-slate-800 rounded-[1.2rem] text-sm font-black text-slate-700 dark:text-slate-200 transition-all hover:border-orange-500 shadow-sm active:scale-95"
            >
              <Filter size={16} className="text-orange-500" />
              <span className="flex items-center gap-1.5">
                {t("filter")}
                {getAppliedFilterCount(filters) > 0 && (
                  <span className="flex items-center justify-center min-w-[18px] h-[18px] text-[10px] text-white bg-orange-500 rounded-full px-1 font-black">
                    {getAppliedFilterCount(filters)}
                  </span>
                )}
              </span>
            </button>

            {getAppliedFilterCount(filters) > 0 && (
              <button
                onClick={handleReset}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-900/30 rounded-[1.2rem] text-sm font-black text-red-600 dark:text-red-400 transition-all hover:bg-red-100 dark:hover:bg-red-500/20 active:scale-95 animate-in fade-in zoom-in duration-200"
              >
                <XCircle size={16} />
                <span>{t("clear")}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <main
        className="flex-1 overflow-y-auto px-4 pt-6 pb-40 md:pb-10 scroll-smooth"
        onScroll={handleScroll}
      >
        {/* Use a grid on desktop: 2 columns on large, 3 on extra large screens */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {orderList?.length > 0 ? (
            orderList?.map((order, index) => (
              <div
                key={index}
                className={`group rounded-[2rem] border p-5 flex flex-col justify-between transition-all cursor-pointer relative overflow-hidden
             ${
               activeId === order.uuid
                 ? "bg-orange-50/50 border-orange-400 ring-4 ring-orange-500/5 shadow-lg shadow-orange-100"
                 : "bg-white dark:bg-[#0A1221] border-slate-100 dark:border-slate-800/50 hover:shadow-md hover:border-slate-200 dark:hover:border-slate-700"
             }`}
                onClick={() => {
                  setActiveId(order.uuid);
                  navigate(`/order-details/${order.uuid}`);
                }}
              >
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="min-w-0">
                      <span className="text-[10px] font-black text-orange-600 dark:text-orange-500 tracking-[0.2em] uppercase">
                        #{order.order_number}
                      </span>
                      <h3 className="text-lg md:text-xl font-black text-slate-800 dark:text-white mt-1">
                        {order.store_name}
                      </h3>
                      <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                        {order.created_at}
                      </p>
                    </div>

                    <span
                      className={`px-4 py-1.5 rounded-full text-[11px] font-bold ${
                        order.status_value === 2
                          ? "bg-emerald-50 text-emerald-600"
                          : "bg-orange-50 text-orange-600"
                      }`}
                    >
                      {order.status_label}
                    </span>
                  </div>

                  {/* Order Info Box */}
                  <div className="bg-slate-50 dark:bg-[#050C1A] rounded-2xl p-4 mb-4 border border-slate-100 dark:border-slate-800/50">
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest">
                          {t("orderType")}
                        </span>
                        <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
                          {order.order_type_label}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest">
                          {t("status")}
                        </span>
                        <span className="text-xs font-black text-slate-700 dark:text-slate-200 uppercase">
                          {order.status_label}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t-2 border-dashed border-slate-200 dark:border-slate-800">
                      <div>
                        <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest leading-none mb-1">
                          {t("pendingAmount")}
                        </p>
                        <span className="text-[13px] font-black text-orange-600 dark:text-orange-400">
                          {formatCurrencyINR(order.pending_amount || 0)}
                        </span>
                      </div>

                      <div className="text-right">
                        <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest mb-1">
                          {t("total")}
                        </p>
                        <p className="text-2xl font-black text-slate-900 dark:text-white leading-none tracking-tighter">
                          {formatCurrencyINR(order.total_amount || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-20 opacity-40">
              <p className="text-lg font-bold text-gray-400">
                {t("NoMatchingOrdersFound")}
              </p>
            </div>
          )}
        </div>
      </main>

      <Drawer
        isOpen={showOffcanvas}
        title={t("filterOrder")}
        onClose={() => setShowOffcanvas(false)}
        footer={
          <>
            <div className="flex gap-3 flex-row justify-end w-full">
              <button className="btn-filter-close" onClick={handleReset}>
                {t("reset")}
              </button>

              <button className="btn-submit-orange" onClick={handleSearch}>
                {t("applyFilters")}
              </button>
            </div>
          </>
        }
      >
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label htmlFor="from_date" className="input-label">
              {t("fromDate")}
            </label>

            <input
              type="date"
              name="from_date"
              className="input-field"
              value={tempFilters.from_date || ""}
              min="2000-01-01"
              max={tempFilters.to_date || "2100-12-31"}
              onChange={(e) => {
                const value = e.target.value;
                if (value && Number(value.split("-")[0]) < 2000) {
                  return;
                }
                setTempFilters((prev) => ({
                  ...prev,
                  from_date: value,
                }));
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="to_date" className="input-label">
              {t("toDate")}
            </label>

            <input
              type="date"
              name="to_date"
              className="input-field"
              value={tempFilters.to_date || ""}
              min={tempFilters.from_date || undefined}
              max="2100-12-31"
              onChange={(e) => {
                const value = e.target.value;
                const year = Number(value.split("-")[0]);
                if (year < 2000) return;
                if (tempFilters.from_date && value < tempFilters.from_date) {
                  return;
                }
                setTempFilters((prev) => ({
                  ...prev,
                  to_date: value,
                }));
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="status" className="input-label">
              {t("status")}
            </label>
            <Select
              options={configData?.order_status}
              value={
                configData?.order_status.find((opt) => opt.value === tempFilters.status) ||
                null
              }
              onChange={(selected) =>
                setTempFilters((prev) => ({
                  ...prev,
                  status: selected?.value ?? "",
                }))
              }
              styles={customStyles}
              placeholder="Select Status"
              isSearchable
              isClearable
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="order_type" className="input-label">
              {t("orderType")}
            </label>
            <Select
              options={configData?.order_type}
              value={
                configData?.order_type.find(
                  (opt) => opt.value === tempFilters.order_type,
                ) || null
              }
              onChange={(selected) =>
                setTempFilters((prev) => ({
                  ...prev,
                  order_type: selected?.value ?? "",
                }))
              }
              styles={customStyles}
              placeholder="Select order type"
              isSearchable
              isClearable
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="store_id" className="input-label">
              {t("store")}
            </label>
            <Select
              options={storeList}
              value={
                storeList.find((opt) => opt.value === tempFilters.store_id) ||
                null
              }
              onChange={(selected) =>
                setTempFilters((prev) => ({
                  ...prev,
                  store_id: selected?.value ?? "",
                }))
              }
              styles={customStyles}
              placeholder="Select store"
              isSearchable
              isClearable
            />
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default OrderManagement;
